import { Disclosure, DisclosureButton, DisclosurePanel, Radio, RadioGroup, Tab, TabGroup, TabList } from '@headlessui/react';
import { useEffect, useState } from 'react';

import { api } from '../api/index.ts';
import type { Entry, EntryMessage } from '../api/types.ts';
import { useT } from '../i18n/index.ts';
import { daysBetween, entryDayLabel, karachiDate, nearDayKey } from '../lib/dates.ts';
import { rs } from '../lib/money.ts';
import { goWalletHome, navigate, useRoute } from '../lib/router.ts';
import { ConfirmDialog } from '../ui/ConfirmDialog.tsx';
import { CategoryIcon, EntityIcon, Icon } from '../ui/icons.tsx';
import { Num, StatusChip } from '../ui/parts.tsx';
import {
  STATUS_FILTERS,
  applyFilters,
  isStatusFilter,
  matchesPlace,
  placeCounts,
  statusCounts,
  type PlaceFilter,
  type StatusFilter,
} from './historyFilter.ts';
import { MessageList, ReplyBox } from './FixThread.tsx';
import { categoryLabel, tintOf } from './labels.ts';
import { ReceiptThumb, ReceiptViewer } from './ReceiptPhotos.tsx';
import { useSpender } from './store.tsx';
import type { Category } from '../api/types.ts';

const chip =
  'inline-flex h-12 shrink-0 cursor-pointer items-center gap-1.5 rounded-full border-[1.5px] border-line-strong bg-paper px-3.5 text-[15px] font-bold whitespace-nowrap text-ink select-none focus:outline-none data-focus:outline-3 data-focus:outline-offset-2 data-focus:outline-ink rtl:text-[16px]';

/** The entry's messages; an older entry (or one from before messages existed) shows its owner note. */
function threadOf(e: Entry): EntryMessage[] {
  if (e.messages?.length) return e.messages;
  if (!e.reviewNote || (e.status !== 'needs_correction' && e.status !== 'rejected')) return [];
  const kind = e.status === 'rejected' ? 'rejected' : 'needs_fix';
  return [{ id: `note-${e.id}`, kind, body: e.reviewNote, authorName: null, createdAt: '' }];
}

function Count({ n, on }: { n: number; on: boolean }) {
  return (
    <span
      className={`num h-[22px] min-w-[22px] rounded-full px-1.5 text-center text-xs leading-[22px] ${on ? 'bg-paper text-ink' : 'bg-ink text-white'}`}
    >
      {n}
    </span>
  );
}

export function History() {
  const { t, lang } = useT();
  const s = useSpender();
  const route = useRoute();
  // Filters live in the address (?status=…&place=…) so they survive opening an entry to fix it.
  const qStatus = route.query.get('status');
  const status: StatusFilter = isStatusFilter(qStatus) ? qStatus : 'all';
  const qPlace = route.query.get('place');
  const place: PlaceFilter = qPlace && s.entities.some((e) => e.id === qPlace) ? qPlace : 'all';
  const setFilters = (next: { status?: StatusFilter; place?: PlaceFilter }) => {
    const q = new URLSearchParams(route.query);
    const st = next.status ?? status;
    const pl = next.place ?? place;
    if (st === 'all') q.delete('status');
    else q.set('status', st);
    if (pl === 'all') q.delete('place');
    else q.set('place', pl);
    const qs = q.toString();
    navigate(`/history${qs ? `?${qs}` : ''}`, { replace: true });
  };

  const [categories, setCategories] = useState<Category[]>([]);
  const [toDelete, setToDelete] = useState<Entry | null>(null);
  const [viewing, setViewing] = useState<Entry | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [sent, setSent] = useState(false);
  // ?open=<id> opens that "Needs fix" entry's messages straight away.
  const openFix = route.query.get('open');
  const today = karachiDate();

  async function replied() {
    await s.refresh();
    setSent(true);
    // The entry is Pending now: follow it there (a "Needs fix" filter would show an empty list).
    const q = new URLSearchParams(route.query);
    q.delete('open');
    if (status === 'needs_correction') q.set('status', 'pending');
    if (q.toString() !== route.query.toString()) navigate(`/history?${q.toString()}`, { replace: true });
  }

  useEffect(() => {
    void Promise.all(s.entities.map((e) => api.categories(e.id))).then((all) => setCategories(all.flat()));
  }, [s.entities]);

  const sCounts = statusCounts(s.entries, place);
  const pCounts = placeCounts(
    s.entries,
    status,
    s.entities.map((e) => e.id),
  );
  const filtered = status !== 'all' || place !== 'all';
  const visible = applyFilters(s.entries, status, place).sort((a, b) => b.txnDate.localeCompare(a.txnDate));
  const groups = [...new Set(visible.map((e) => e.txnDate))].map((day) => {
    const rows = visible.filter((e) => e.txnDate === day);
    return { day, rows, total: rows.filter((e) => e.status !== 'rejected').reduce((a, e) => a + e.amount, 0) };
  });
  // "This week" follows the company/house picked.
  const weekTotal = s.entries
    .filter((e) => matchesPlace(e, place) && e.status !== 'rejected' && daysBetween(e.txnDate, today) < 7)
    .reduce((a, e) => a + e.amount, 0);
  const statusLabel = (f: StatusFilter) => (f === 'all' ? t('history.all') : f === 'unsent' ? t('history.notSent') : t(`status.${f}`));

  const dayTitle = (iso: string) => {
    const near = nearDayKey(iso, today);
    return near ? t(near) : entryDayLabel(iso, today, lang);
  };

  async function remove(e: Entry) {
    setDeleting(true);
    try {
      await api.deleteEntry(e.id, e.entityId);
      await s.refresh();
    } finally {
      setDeleting(false);
      setToDelete(null);
    }
  }

  return (
    <div className="flex h-app flex-col">
      <header className="safe-top shrink-0 border-b-[1.5px] border-line bg-paper">
        <div className="w-full sm:px-2 lg:px-6">
          <div className="flex h-[60px] items-center gap-1.5 ps-1 pe-4">
            <button type="button" aria-label={t('add.back')} onClick={goWalletHome} className="flex size-12 items-center justify-center">
              <Icon name="arrowStart" size={24} stroke={2.25} flip />
            </button>
            <h1 className="m-0 flex-1 text-[21px] font-bold rtl:text-[23px]">{t('history.title')}</h1>
            <span className="flex flex-col items-end leading-tight">
              <span className="text-xs font-semibold text-muted">{t('history.thisWeek')}</span>
              <Num className="text-[17px] font-bold">{rs(weekTotal)}</Num>
            </span>
          </div>
          {/* Status: All · Pending · Approved · Needs fix · Rejected · Not sent (counts within the place picked). */}
          <TabGroup selectedIndex={STATUS_FILTERS.indexOf(status)} onChange={(i) => setFilters({ status: STATUS_FILTERS[i] ?? 'all' })}>
            <TabList aria-label={t('history.filterStatus')} className="flex gap-2 overflow-x-auto px-4 pt-0.5 pb-2.5">
              {STATUS_FILTERS.map((key) => (
                <Tab
                  key={key}
                  className={`${chip} data-selected:border-ink data-selected:bg-ink data-selected:text-white ${
                    key === 'needs_correction' && sCounts[key] > 0 && status !== key ? 'border-warning' : ''
                  }`}
                >
                  {statusLabel(key)}
                  <Count n={sCounts[key]} on={status === key} />
                </Tab>
              ))}
            </TabList>
          </TabGroup>
          {/* Company / house: only when the spender works for more than one. */}
          {s.entities.length > 1 && (
            <RadioGroup
              value={place}
              onChange={(p: PlaceFilter) => setFilters({ place: p })}
              aria-label={t('history.filterPlace')}
              className="flex gap-2 overflow-x-auto border-t border-line px-4 pt-2.5 pb-3"
            >
              <Radio value="all" className={`${chip} data-checked:border-ink data-checked:bg-ink data-checked:text-white`}>
                {t('history.allPlaces')}
                <Count n={pCounts.all ?? 0} on={place === 'all'} />
              </Radio>
              {s.entities.map((e) => {
                const on = place === e.id;
                return (
                  <Radio
                    key={e.id}
                    value={e.id}
                    className={`${chip} ${on ? 'text-white' : ''}`}
                    style={on ? { background: e.colour, borderColor: e.colour } : undefined}
                  >
                    <span style={on ? undefined : { color: e.colour }}>
                      <EntityIcon entity={e} size={18} />
                    </span>
                    {e.name}
                    <Count n={pCounts[e.id] ?? 0} on={on} />
                  </Radio>
                );
              })}
            </RadioGroup>
          )}
        </div>
      </header>

      <main className="safe-bottom min-h-0 flex-1 overflow-y-auto">
        <div className="w-full sm:px-6 lg:px-10">
          {sent && (
            <div
              role="status"
              className="mx-4 mt-3 flex items-center gap-2.5 rounded-xl border-[1.5px] border-success/40 bg-success-tint px-3.5 py-2.5 text-[15px] font-bold text-success-text sm:mx-0 rtl:text-[17px]"
            >
              <Icon name="check" size={20} stroke={2.5} />
              <span className="flex-1">{t('history.replySent')}</span>
              <button
                type="button"
                aria-label={t('add.close')}
                onClick={() => setSent(false)}
                className="-me-2 flex size-12 shrink-0 items-center justify-center rounded-lg hover:bg-black/5"
              >
                <Icon name="close" size={18} />
              </button>
            </div>
          )}
          {groups.length === 0 && (
            <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
              <p className="m-0 text-[15px] text-muted rtl:text-[17px]">{filtered ? t('history.noMatch') : t('history.nothing')}</p>
              {filtered && (
                <button
                  type="button"
                  onClick={() => setFilters({ status: 'all', place: 'all' })}
                  className="h-12 rounded-xl border-[1.5px] border-line-strong bg-paper px-4 text-[15px] font-bold hover:bg-ground"
                >
                  {t('history.clearFilters')}
                </button>
              )}
            </div>
          )}
          {groups.map((g) => (
            <section key={g.day}>
              <div className="flex items-baseline justify-between px-4 pt-4 pb-2 sm:px-1 sm:pt-6">
                <h2 className="m-0 text-[15px] font-bold">{dayTitle(g.day)}</h2>
                <Num className="text-[15px] font-semibold text-muted">{rs(g.total)}</Num>
              </div>
              <ul className="m-0 list-none overflow-hidden border-y-[1.5px] border-line bg-paper p-0 sm:rounded-2xl sm:border-[1.5px]">
                {g.rows.map((e) => {
                  const ent = s.entityById(e.entityId);
                  const cat = categories.find((c) => c.id === e.categoryId);
                  const fix = e.status === 'needs_correction';
                  // With a receipt photo, the photo takes the icon's place (its own button opens it).
                  const photo = (e.receiptIds?.length ?? 0) > 0;
                  const rowBg = fix ? 'bg-[#FFFBEB]' : 'bg-paper';
                  const rowCls = `flex min-h-[76px] w-full min-w-0 items-center gap-3 py-2.5 text-start focus:outline-none data-focus:outline-3 data-focus:-outline-offset-3 data-focus:outline-ink focus-visible:outline-3 focus-visible:-outline-offset-3 focus-visible:outline-ink ${
                    photo ? 'ps-3 pe-4' : 'px-4'
                  } ${fix ? `${rowBg} hover:bg-ground` : rowBg}`;
                  const lead = photo ? (
                    <span className={`flex shrink-0 items-center ps-4 ${rowBg}`}>
                      <ReceiptThumb entry={e} onOpen={() => setViewing(e)} />
                    </span>
                  ) : null;
                  const body = (
                    <>
                      {!photo && (
                        <span
                          className="flex size-[46px] shrink-0 items-center justify-center rounded-xl"
                          style={{ background: tintOf(ent?.colour ?? '#13211F'), color: ent?.colour }}
                        >
                          <CategoryIcon icon={cat?.icon ?? null} />
                        </span>
                      )}
                      <span className="flex min-w-0 flex-1 flex-col leading-snug">
                        <span className="truncate text-[17px] font-bold">{e.itemName}</span>
                        <span className="text-[13px] text-muted">
                          {cat ? categoryLabel(cat, t) : ''} · {ent?.name}
                          {e.quantity ? (
                            <>
                              {' '}
                              · <Num>{e.quantity}</Num> {t(`units.${e.unit ?? 'pcs'}`)}
                            </>
                          ) : null}
                        </span>
                        {fix && (
                          <span className="text-[13px] font-bold text-warning-text">
                            {e.reviewNote ? `${e.reviewNote} · ` : ''}
                            {t('history.tapToReply')}
                          </span>
                        )}
                        {e.status === 'rejected' && e.reviewNote && <span className="text-[13px] text-danger-text">{e.reviewNote}</span>}
                        {e.paidByEntityId && (
                          <span className="text-[13px] font-semibold text-ink rtl:text-[15px]">
                            {s.entityById(e.paidByEntityId)
                              ? t('history.paidBy', { name: s.entityById(e.paidByEntityId)?.name ?? '' })
                              : t('history.paidByOther')}
                          </span>
                        )}
                      </span>
                      <span className="flex flex-col items-end gap-1">
                        <Num className={`text-[17px] font-bold ${e.status === 'rejected' ? 'line-through' : ''}`}>{rs(e.amount)}</Num>
                        <StatusChip entry={e} />
                      </span>
                    </>
                  );
                  return (
                    <li key={e.id} className="border-b border-line last:border-b-0">
                      {fix ? (
                        // Needs fixing: the row opens the owner's messages, a reply box and "Fix the entry".
                        <Disclosure defaultOpen={openFix === e.id}>
                          <div className="flex">
                            {lead}
                            <DisclosureButton className={`${rowCls} data-open:bg-[#FEF3C7]`}>{body}</DisclosureButton>
                          </div>
                          <DisclosurePanel className="flex flex-col gap-4 border-t border-warning/20 bg-[#FFFBEB] px-4 pt-3 pb-4">
                            <MessageList messages={threadOf(e)} />
                            <ReplyBox entry={e} onSent={replied} />
                            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-warning/20 pt-3">
                              <span className="text-[13px] text-muted rtl:text-[15px]">{t('history.orFix')}</span>
                              <button
                                type="button"
                                onClick={() => navigate(`/add?fix=${e.id}&step=proof`)}
                                className="inline-flex h-12 items-center gap-2 rounded-xl border-[1.5px] border-line-strong bg-paper px-4 text-[15px] font-bold hover:bg-ground focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ink"
                              >
                                <Icon name="pencil" size={18} />
                                {t('history.fixEntry')}
                              </button>
                            </div>
                          </DisclosurePanel>
                        </Disclosure>
                      ) : e.status === 'pending' ? (
                        // Still pending: tap to show Edit / Delete.
                        <Disclosure>
                          <div className="flex">
                            {lead}
                            <DisclosureButton className={`${rowCls} hover:bg-ground data-open:bg-ground`}>{body}</DisclosureButton>
                          </div>
                          <DisclosurePanel
                            transition
                            className="flex flex-wrap justify-end gap-2.5 bg-ground px-4 pb-3 transition duration-150 ease-out data-closed:-translate-y-1 data-closed:opacity-0"
                          >
                            {threadOf(e).length > 0 && (
                              <div className="w-full pb-1">
                                <MessageList messages={threadOf(e)} />
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={() => navigate(`/add?fix=${e.id}&step=proof`)}
                              className="inline-flex h-12 items-center gap-2 rounded-xl border-[1.5px] border-line-strong bg-paper px-4 text-[15px] font-bold focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ink"
                            >
                              <Icon name="pencil" size={18} />
                              {t('history.edit')}
                            </button>
                            <button
                              type="button"
                              onClick={() => setToDelete(e)}
                              className="inline-flex h-12 items-center gap-2 rounded-xl border-[1.5px] border-danger bg-paper px-4 text-[15px] font-bold text-danger focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ink"
                            >
                              <Icon name="trash" size={18} />
                              {t('history.delete')}
                            </button>
                          </DisclosurePanel>
                        </Disclosure>
                      ) : threadOf(e).length > 0 ? (
                        // Decided after a conversation (even a single owner note): tap to read it.
                        <Disclosure>
                          <div className="flex">
                            {lead}
                            <DisclosureButton className={`${rowCls} hover:bg-ground data-open:bg-ground`}>{body}</DisclosureButton>
                          </div>
                          <DisclosurePanel className="bg-ground px-4 pt-1 pb-3">
                            <MessageList messages={threadOf(e)} />
                          </DisclosurePanel>
                        </Disclosure>
                      ) : (
                        <div className="flex">
                          {lead}
                          <div className={rowCls}>{body}</div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      </main>

      <ReceiptViewer entry={viewing} onClose={() => setViewing(null)} />

      <ConfirmDialog
        open={toDelete !== null}
        icon="trash"
        title={t('history.deleteTitle')}
        body={
          toDelete && !toDelete.paidByEntityId
            ? t('history.deleteConfirm', {
                amount: rs(toDelete.amount),
                entity: s.entityById(toDelete.entityId)?.name ?? '',
              })
            : ''
        }
        confirmLabel={t('history.delete')}
        cancelLabel={t('app.cancel')}
        busy={deleting}
        onCancel={() => setToDelete(null)}
        onConfirm={() => toDelete && void remove(toDelete)}
      />
    </div>
  );
}
